import type {
  FieldFormat,
  FormatSpec,
  LogicalDataType,
  ScalarValue,
  TimeGrain,
} from '@aibi/contracts';
import {
  inferExpressionType,
  isCalculatedMeasure,
  typeFamily,
  type ModelMeasure,
  type SemanticModelIndex,
} from '@aibi/semantic-model';

/** Tipos sin orden ni igualdad fiable: no se pueden filtrar por valor ni ordenar. */
export const NON_COMPARABLE_TYPES: ReadonlySet<LogicalDataType> = new Set([
  'json',
  'binary',
  'geography',
]);

/** Resuelve un formato de campo (inline o referencia al catalogo) a su especificacion. */
export function resolveFormat(
  format: FieldFormat | undefined,
  index: SemanticModelIndex,
): FormatSpec | null {
  if (!format) return null;
  if (format.type === 'ref') return index.getFormat(format.formatId)?.spec ?? null;
  return format;
}

/** Tipo logico del resultado de una medida (base o calculada). */
export function measureDataType(measure: ModelMeasure, index: SemanticModelIndex): LogicalDataType {
  return isCalculatedMeasure(measure)
    ? inferExpressionType(measure.expression, index)
    : inferExpressionType({ type: 'measure', measureId: measure.id }, index);
}

/** `true` si el grano temporal es aplicable al tipo (las horas requieren fecha y hora). */
export function grainSupportsType(grain: TimeGrain, dataType: LogicalDataType): boolean {
  if (dataType === 'unknown' || dataType === 'datetime') return true;
  return dataType === 'date' && grain !== 'hour';
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * `true` si un valor no nulo es compatible con el tipo del campo filtrado. Con grano
 * temporal se aceptan etiquetas de periodo (`2024-Q1`) o numeros (`2024`).
 */
export function valueMatchesType(
  value: Exclude<ScalarValue, null>,
  dataType: LogicalDataType,
  grain: TimeGrain | null,
): boolean {
  if (grain !== null) return typeof value === 'string' || typeof value === 'number';
  switch (typeFamily(dataType)) {
    case 'numeric':
      return typeof value === 'number' && Number.isFinite(value);
    case 'text':
      return typeof value === 'string';
    case 'boolean':
      return typeof value === 'boolean';
    case 'temporal':
      return typeof value === 'string' && ISO_DATE.test(value) && !Number.isNaN(Date.parse(value));
    case 'other':
      return dataType === 'time' || dataType === 'duration'
        ? typeof value === 'string' || typeof value === 'number'
        : true;
    case 'unknown':
      return true;
  }
}
